import * as React from "react";
import { I18n } from "react-redux-i18n";

import { NewTableColumn, NewTableGetId, NewTableLoadMetadata } from "@common/components/new-table";
import { HttpErrorDto } from "@common/dto/http";
import { getTestAttributes } from "@common/utils/tests";
import { QuestionnaireListDto } from "@common/dto/questionnaire/list-dto";
import { FormControl } from "@common/components/form-control";
import { MODULE_NAME } from "@modules/invite-status/constants/shared";
import { InviteHistoryDto } from "@common/dto/invite-history/list-dto";
import { InviteListDto } from "@common/dto/invite/list-dto";
import { InviteDatasheetAdditionalDataUpdateDto } from "@common/dto/invite/datasheet/additional-data-update-dto";
import { InviteStatusIndividualListDto, InviteStatusIndividual } from "@modules/invite-status/dto/individuals/list-dto";

export type RelativeChooserProps = {
  saveAdditionalDatasheetFormatData: (data: InviteDatasheetAdditionalDataUpdateDto) => Promise<void>;
  questionnaire: QuestionnaireListDto;
  showErrorMessage: (message: string) => void;
  getHistoryLogs: (params: {
    inviteId?: InviteListDto["id"];
    globalId: InviteStatusIndividual["id"];
    fhq4?: InviteListDto["fhq4"];
  }) => Promise<Array<InviteHistoryDto>>;
  getFhqInviteRelatives: (params: { start: number }) => Promise<InviteStatusIndividualListDto>;
  selectedIndividuals: InviteStatusIndividual[];
  onChange: (individuals: InviteStatusIndividual[]) => void;
};

type TableColumnKey = keyof Pick<
  InviteStatusIndividual,
  "individualName" | "firstName" | "lastName" | "emailAddress" | "passCode" | "phoneNumber"
>;

export const useRelativeChooserController = (props: RelativeChooserProps) => {
  const {
    onChange,
    getFhqInviteRelatives,
    getHistoryLogs,
    showErrorMessage,
    saveAdditionalDatasheetFormatData,
    selectedIndividuals,
  } = props;
  const [individualsTotalCount, setTotalCount] = React.useState<number>(0);
  const [individuals, setIndividuals] = React.useState<Array<InviteStatusIndividual>>([]);
  const [fields, setFields] = React.useState<InviteStatusIndividualListDto["fields"] | undefined>();
  const [individualsLoadMetadata, setIndividualsLoadMetadata] = React.useState<NewTableLoadMetadata>({
    pagination: { size: 45, start: 0 },
  });
  const [loadingIndividuals, setLoadingIndividuals] = React.useState(false);
  const [loadingHistoryLogs, setLoadingHistoryLogs] = React.useState(false);
  const [historyLogs, setHistoryLogs] = React.useState<Array<InviteHistoryDto>>([]);

  const itemUniqProperty = "id";

  const selectedIndividual = React.useMemo(
    () => selectedIndividuals[selectedIndividuals.length - 1],
    [selectedIndividuals]
  );

  const handleLoadData = async (
    params: NewTableLoadMetadata & {
      loadedIndividuals: Array<InviteStatusIndividual>;
    }
  ) => {
    try {
      setLoadingIndividuals(true);

      const { pagination } = params;

      const relativesData = await getFhqInviteRelatives({
        start: params.pagination.start,
      });

      const finalData =
        pagination.start > 0
          ? [...params.loadedIndividuals, ...relativesData.searchresultindividuals]
          : relativesData.searchresultindividuals;

      setIndividuals(finalData);
      setTotalCount((x) => (pagination.start > 0 ? x : relativesData.globalcount));
      setFields(relativesData.fields);
      setIndividualsLoadMetadata({
        pagination,
      });
    } finally {
      setLoadingIndividuals(false);
    }
  };

  const initialize = async () => {
    try {
      await handleLoadData({
        loadedIndividuals: [],
        pagination: individualsLoadMetadata.pagination,
      });
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto | undefined)?.entity || I18n.t("Failed to get data");
      showErrorMessage(errorMessage);
    }
  };

  React.useEffect(() => {
    initialize();
  }, []);

  const handleSelectedItemsChange = React.useCallback(
    async (items: Array<InviteStatusIndividual>) => {
      if (!items.length) {
        return;
      }

      const selectedIndividual = items[0]!;

      onChange(items);

      try {
        setLoadingHistoryLogs(true);

        const res = await getHistoryLogs({
          globalId: selectedIndividual.id,
        });
        setHistoryLogs(res);
      } catch (e) {
        console.error(e);
        showErrorMessage((e as HttpErrorDto | undefined)?.entity || I18n.t("Failed to load individual history logs"));
      } finally {
        setLoadingHistoryLogs(false);
      }
    },
    [onChange, getHistoryLogs, showErrorMessage]
  );

  const getId = React.useMemo(
    (): NewTableGetId => ({
      header: () => getTestAttributes({ id: "bw-quest-invites-step4-sel-ind-list-thead-row0" }).id,
      headerCell: (cell, index) =>
        getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-thead-row0-cell${index}` }).id,
      bodyRow: (row, index) => getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-tbody-row${index}` }).id,
      bodyCell: (row, rowIndex, cell, cellIndex) =>
        getTestAttributes({ id: `bw-quest-invites-step4-sel-ind-list-tbody-row${rowIndex}-cell${cellIndex}` }).id,
    }),
    []
  );

  const handleFieldChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { columnId } = e.target.dataset;
    const value = e.target.value.trim();

    const changedField = fields?.[columnId as string];

    if (!selectedIndividual || !changedField) {
      return;
    }

    const data: InviteDatasheetAdditionalDataUpdateDto = {
      fieldId: changedField.id,
      system: changedField.system,
      context: changedField.context,
      globalId: selectedIndividual.id as unknown as number,
      dataValue: value
    };

    try {
      await saveAdditionalDatasheetFormatData(data);
      const updatedIndividual = {
        ...selectedIndividual,
        [columnId as string]: value,
      };

      const updateSelectedIndividual = (x: InviteStatusIndividual) => {
        if (x.id.toString() === selectedIndividual.id.toString()) {
          return updatedIndividual;
        }

        return x;
      }

      setIndividuals(individuals.map(updateSelectedIndividual));
      onChange(selectedIndividuals.map(updateSelectedIndividual));

    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to update individual"));
    }
  };

  const renderInput = React.useCallback(
    ({
      row: { original },
      column,
    }: {
      row: { original: InviteStatusIndividual };
      column: { id: string; columnDef: { accessorKey: string } };
    }) => {
      return (
        <FormControl
          {...getTestAttributes(
            `${MODULE_NAME}_invite_patient_ind_chooser`,
            "table_cell",
            "input",
            `${column.columnDef.accessorKey}_${original.id}`
          )}
          className="InviteStatusRelativeChooser__table__input"
          defaultValue={(original as unknown as Record<string, string>)[column.columnDef.accessorKey]}
          data-column-id={column.id}
          onBlur={handleFieldChange}
        />
      );
    },
    [handleFieldChange]
  );

  const processedColumns = React.useMemo(() => {
    const columns: Array<{ header: string; accessorKey: TableColumnKey }> = [
      {
        header: "Individual name",
        accessorKey: "individualName",
      },
      {
        header: "First name",
        accessorKey: "firstName",
      },
      {
        header: "Last name",
        accessorKey: "lastName",
      },
      {
        header: "Email",
        accessorKey: "emailAddress",
      }
    ];

    return columns.map((item) => ({
      id: item.accessorKey,
      header: I18n.t(item.header),
      accessorKey: item.accessorKey,
      minSize: 20,
      disableActions: true,
      enableResizing: false,
      enableSorting: false,
      cell: (fields?.[item.accessorKey]
        ? renderInput
        : undefined) as NewTableColumn<InviteStatusIndividual>["cell"],
    }));
  }, [renderInput, fields]);

  return {
    processedColumns,
    individualsLoadMetadata,
    loadingIndividuals,
    loadingHistoryLogs,
    individuals,
    historyLogs,
    handleSelectedItemsChange,
    getId,
    itemUniqProperty,
    handleLoadData,
    individualsTotalCount,
    selectedIndividual,
  };
};
